import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { PRODUCTS as MOCK, BUNDLES } from '../data/mock';
import { SHOPIFY_ENABLED, cartCreate, cartLinesAdd, cartLinesUpdate, cartLinesRemove, fetchCart, fetchProductByHandle } from '../lib/shopify';

const StoreCtx = createContext(null);
const load = (k, f) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : f; } catch { return f; } };

export function StoreProvider({ children }) {
  const [cart, setCart] = useState(() => load('ao_cart', []));
  const [wishlist, setWishlist] = useState(() => load('ao_wish', []));
  const [cartOpen, setCartOpen] = useState(false);
  const [shopifyCart, setShopifyCart] = useState(null);
  const productCache = useRef(new Map());
  const [, force] = useState(0);

  useEffect(() => { localStorage.setItem('ao_cart', JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem('ao_wish', JSON.stringify(wishlist)); }, [wishlist]);

  // Restore Shopify cart on load
  useEffect(() => {
    if (!SHOPIFY_ENABLED) return;
    const id = localStorage.getItem('ao_sh_cart');
    if (!id) return;
    fetchCart(id).then((c) => { if (c) setShopifyCart(c); else localStorage.removeItem('ao_sh_cart'); }).catch(() => localStorage.removeItem('ao_sh_cart'));
  }, []);

  const findItem = useCallback((id) => {
    if (productCache.current.has(id)) return productCache.current.get(id);
    const m = MOCK.find((p) => p.id === id) || BUNDLES.find((b) => b.id === id);
    if (m) return m;
    // Try fetch from Shopify
    if (SHOPIFY_ENABLED) {
      fetchProductByHandle(id).then((p) => { if (p) { productCache.current.set(id, p); force((x) => x + 1); } }).catch(() => {});
    }
    return null;
  }, []);

  const addToCart = useCallback(async (id, qty = 1, product = null) => {
    if (product) productCache.current.set(id, product);
    // Local cart (used for bundles & fallback display)
    setCart((prev) => {
      const i = prev.findIndex((x) => x.id === id);
      if (i >= 0) { const n = [...prev]; n[i] = { ...n[i], qty: n[i].qty + qty }; return n; }
      return [...prev, { id, qty }];
    });
    setCartOpen(true);

    // Push to Shopify cart if available
    if (SHOPIFY_ENABLED && product?.variantId) {
      try {
        const existing = shopifyCart?.id || localStorage.getItem('ao_sh_cart');
        if (existing) {
          const c = await cartLinesAdd(existing, [{ merchandiseId: product.variantId, quantity: qty }]);
          if (c) { setShopifyCart(c); localStorage.setItem('ao_sh_cart', c.id); }
        } else {
          const c = await cartCreate([{ merchandiseId: product.variantId, quantity: qty }]);
          if (c) { setShopifyCart(c); localStorage.setItem('ao_sh_cart', c.id); }
        }
      } catch (e) { console.warn('Shopify add failed', e); }
    }
  }, [shopifyCart]);

  const removeFromCart = async (id) => {
    setCart((p) => p.filter((x) => x.id !== id));
    if (SHOPIFY_ENABLED && shopifyCart) {
      const prod = findItem(id);
      const line = shopifyCart.lines?.edges?.find((e) => e.node.merchandise.id === prod?.variantId);
      if (line) {
        const c = await cartLinesRemove(shopifyCart.id, [line.node.id]);
        if (c) setShopifyCart(c);
      }
    }
  };

  const updateQty = async (id, qty) => {
    if (qty <= 0) return removeFromCart(id);
    setCart((p) => p.map((x) => (x.id === id ? { ...x, qty } : x)));
    if (SHOPIFY_ENABLED && shopifyCart) {
      const prod = findItem(id);
      const line = shopifyCart.lines?.edges?.find((e) => e.node.merchandise.id === prod?.variantId);
      if (line) {
        const c = await cartLinesUpdate(shopifyCart.id, [{ id: line.node.id, quantity: qty }]);
        if (c) setShopifyCart(c);
      }
    }
  };

  const clearCart = () => { setCart([]); setShopifyCart(null); localStorage.removeItem('ao_sh_cart'); };
  const toggleWish = (id) => setWishlist((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const subtotal = cart.reduce((s, c) => { const it = findItem(c.id); return s + (it ? it.price * c.qty : 0); }, 0);
  const count = cart.reduce((s, c) => s + c.qty, 0);
  const checkoutUrl = shopifyCart?.checkoutUrl || null;

  return (
    <StoreCtx.Provider value={{ cart, wishlist, cartOpen, setCartOpen, addToCart, removeFromCart, updateQty, clearCart, toggleWish, findItem, subtotal, count, checkoutUrl, shopifyCart, cacheProduct: (p) => productCache.current.set(p.id, p) }}>
      {children}
    </StoreCtx.Provider>
  );
}

export const useStore = () => useContext(StoreCtx);
